import React, { Fragment } from 'react';
import classNames from 'classnames';
import { LuListFilter, LuChevronDown, LuSearch, LuSlidersHorizontal } from 'react-icons/lu';
import { Popover, Transition } from '@headlessui/react';
import { useTheme } from '@/context/theme.context';
import { IconBadge } from '@/components/atoms/IconBadge';
import { GradientDivider } from '@/components/atoms/GradientDivider';
import { APP_BG, APP_BORDER, APP_TEXT } from '@/constant/theme/colors.constant';

// ─── Types ────────────────────────────────────────────────────────────────────

export type PageToolbarSize = 'sm' | 'md';

export interface PageToolbarProps {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  avatarNode?: React.ReactNode;
  count?: number;
  size?: PageToolbarSize;
  className?: string;

  actionsSlot?: React.ReactNode;
  actions?: React.ReactNode;
  /** Always pinned to the title bar at every breakpoint (buttons, toggles, exports). */
  actionButtons?: React.ReactNode;
  /** Always visible in title bar; renders LEFT of search on desktop (e.g. auto-refresh toggle). */
  leadingActions?: React.ReactNode;
  rightContent?: React.ReactNode;
  pills?: React.ReactNode;

  filtersLeft?: React.ReactNode;
  filtersRight?: React.ReactNode;
  filtersSlot?: React.ReactNode;

  showDivider?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

const PageToolbar: React.FC<PageToolbarProps> = ({
  title,
  subtitle,
  icon,
  avatarNode,
  size = 'md',
  className,
  actionsSlot,
  actions,
  actionButtons,
  leadingActions,
  rightContent,
  pills,
  filtersLeft,
  filtersRight,
  filtersSlot,
  showDivider = true,
}) => {
  const { colors } = useTheme();
  const gradientBg = `linear-gradient(90deg, ${colors.primary}, ${colors.secondary}, ${colors.tertiary})`;

  const hasFilters = filtersLeft != null || filtersRight != null || filtersSlot != null;
  const allActions = actionsSlot ?? actions;
  const isCompact = size === 'sm';
  const filterCount = [filtersLeft, filtersSlot, filtersRight].filter(Boolean).length;

  // Mobile/tablet bar shows when there is search OR filters (or both)
  const hasMobileBar = allActions != null || hasFilters;
  const mobileBarIcon = allActions && hasFilters
    ? <LuSlidersHorizontal className="w-3.5 h-3.5 shrink-0 text-primary" />
    : allActions
      ? <LuSearch className="w-3.5 h-3.5 shrink-0 text-primary" />
      : <LuListFilter className="w-3.5 h-3.5 shrink-0 text-primary" />;
  const mobileBarLabel = allActions && hasFilters ? 'Search & Filters' : allActions ? 'Search' : 'Filters';

  const leading =
    avatarNode ??
    (icon ? (
      <IconBadge
        icon={icon}
        size={isCompact ? 'xs' : 'md'}
        variant="solid"
        shadow={false}
        customStyle={isCompact ? undefined : { width: '38px', height: '38px', borderRadius: '12px' }}
        className={isCompact ? 'w-7 h-7 rounded-lg' : undefined}
      />
    ) : null);

  return (
    <div
      className={classNames(
        'relative flex-shrink-0 rounded-t-xl shadow-sm',
        `border ${APP_BORDER.card}`,
        APP_BG.base,
        className,
      )}
    >
      {/* ══════════════════════════════════════════════════════════════
          TITLE ROW
          lg+  : icon | title/subtitle/pills | rightContent | actions (inline)
          < lg : icon | title/subtitle/pills | rightContent only
                 (search + actions live in the mobile bar below)
      ══════════════════════════════════════════════════════════════ */}
      <div
        className={classNames(
          'flex items-center gap-3 rounded-t-xl',
          isCompact ? 'px-4 py-2.5' : 'px-4 sm:px-5 py-3',
        )}
        style={{ background: `linear-gradient(to right, ${colors.primary}12 0%, transparent 60%)` }}
      >
        {leading && <div className="shrink-0">{leading}</div>}

        <div className="flex-1 min-w-0">
          <h2
            className={classNames(
              'truncate font-semibold leading-none tracking-tight',
              APP_TEXT.default,
              isCompact ? 'text-[13px]' : 'text-[15px]',
            )}
          >
            {title}
          </h2>
          {subtitle && (
            <p className={classNames('mt-[3px] leading-none truncate text-[11px]', APP_TEXT.muted)}>
              {subtitle}
            </p>
          )}
          {pills && (
            <div className="flex flex-wrap items-center gap-1 mt-1.5">{pills}</div>
          )}
        </div>

        {rightContent && (
          <div className="hidden lg:flex items-center shrink-0 gap-3">{rightContent}</div>
        )}

        {/* Leading actions — desktop lg+: left of search (e.g. auto-refresh toggle) */}
        {leadingActions && (
          <div className="hidden lg:flex items-center gap-2 shrink-0">{leadingActions}</div>
        )}

        {/* Desktop lg+: show search inline in the title bar */}
        {allActions && (
          <div className="hidden lg:flex items-center gap-2 shrink-0">{allActions}</div>
        )}

        {/* Divider between search group and buttons group (desktop only) */}
        {actionButtons && allActions && (
          <div className="hidden lg:block h-6 w-px bg-gray-200 dark:bg-white/[0.08] shrink-0" />
        )}

        {/* Action buttons — desktop lg+: natural size, rendered RIGHT of search */}
        {actionButtons && (
          <div className="hidden lg:flex items-center gap-2 shrink-0">{actionButtons}</div>
        )}

        {/* Leading actions + action buttons — mobile/tablet < lg: xs compact */}
        {(leadingActions || actionButtons) && (
          <div className="flex lg:hidden items-center gap-1 shrink-0 [&_button:not(.toggle-track)]:!h-7 [&_button:not(.toggle-track)]:!px-2 [&_button:not(.toggle-track)]:!text-[10px] [&_button:not(.toggle-track)]:!gap-0.5 [&_button:not(.toggle-track)_svg]:!w-3 [&_button:not(.toggle-track)_svg]:!h-3">
            {leadingActions}
            {actionButtons}
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════
          MOBILE / TABLET BAR  (hidden on lg+)
          Single collapsible popover that combines:
            – search / action buttons (actionsSlot)  ← top section
            – filter dropdowns (filtersLeft/Right/Slot) ← bottom section
          If the page has no filters, the bar label is "Search".
          If the page has no search, the bar label is "Filters".
          If both, the bar label is "Search & Filters".
      ══════════════════════════════════════════════════════════════ */}
      {hasMobileBar && (
        <div
          className={classNames(
            'flex lg:hidden px-4 py-2.5',
            `border-t ${APP_BORDER.filterRow}`,
            APP_BG.canvas,
          )}
        >
          <Popover className="relative w-full">
            {({ open }) => (
              <>
                <Popover.Button
                  className={classNames(
                    'flex items-center gap-2 w-full h-10 px-3.5 rounded-xl',
                    'border shadow-sm transition-all duration-150 focus:outline-none',
                    open
                      ? 'border-primary/50 ring-2 ring-primary/15 bg-primary/[0.04] dark:bg-primary/[0.07]'
                      : `border-gray-200 dark:border-white/[0.08] ${APP_BG.base}`,
                  )}
                >
                  {mobileBarIcon}
                  <span className={classNames('text-[12px] font-medium flex-1 text-left', APP_TEXT.muted)}>
                    {mobileBarLabel}
                  </span>
                  {filterCount > 0 && (
                    <span className="inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full text-[10px] font-bold bg-primary/15 text-primary border border-primary/20">
                      {filterCount}
                    </span>
                  )}
                  <LuChevronDown
                    className={classNames(
                      'w-3.5 h-3.5 shrink-0 transition-transform duration-150',
                      APP_TEXT.subtle,
                      open ? 'rotate-180' : '',
                    )}
                  />
                </Popover.Button>

                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-200"
                  enterFrom="opacity-0 -translate-y-1"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in duration-150"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 -translate-y-1"
                >
                  <Popover.Panel
                    className={classNames(
                      'absolute left-0 right-0 top-full z-50 mt-1.5',
                      'rounded-xl shadow-xl',
                      APP_BG.elevated,
                      'border border-gray-200 dark:border-white/[0.08]',
                    )}
                  >
                    <div className="h-[2px] w-full rounded-t-xl" style={{ background: gradientBg }} />
                    <div className="p-3 flex flex-col gap-3">
                      {/* ── Search / action buttons ── */}
                      {allActions && (
                        <div
                          className={classNames(
                            'flex flex-col gap-2',
                            hasFilters ? `pb-3 border-b ${APP_BORDER.filterRow}` : '',
                          )}
                        >
                          {allActions && hasFilters && (
                            <p className={classNames('text-[10px] font-semibold uppercase tracking-widest', APP_TEXT.subtle)}>
                              Search
                            </p>
                          )}
                          {allActions}
                        </div>
                      )}
                      {/* ── Filter dropdowns ── */}
                      {(filtersLeft || filtersSlot) && (
                        <div className="flex flex-col gap-2">
                          {allActions && (
                            <p className={classNames('text-[10px] font-semibold uppercase tracking-widest', APP_TEXT.subtle)}>
                              Filters
                            </p>
                          )}
                          <div className="flex flex-wrap gap-2">
                            {filtersLeft}
                            {filtersSlot}
                          </div>
                        </div>
                      )}
                      {filtersRight && (
                        <div
                          className={classNames(
                            'flex flex-wrap gap-2',
                            (filtersLeft || filtersSlot) ? `pt-2.5 border-t ${APP_BORDER.filterRow}` : '',
                          )}
                        >
                          {filtersRight}
                        </div>
                      )}
                    </div>
                  </Popover.Panel>
                </Transition>
              </>
            )}
          </Popover>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          DESKTOP FILTER BAR  (lg+ only, inline)
      ══════════════════════════════════════════════════════════════ */}
      {hasFilters && (
        <div
          className={classNames(
            'hidden lg:flex flex-wrap items-center gap-2 px-4 lg:px-5 py-2',
            `border-t ${APP_BORDER.filterRow}`,
            APP_BG.filter,
          )}
        >
          <IconBadge
            icon={<LuListFilter className="w-3.5 h-3.5" />}
            size="xs"
            variant="outline"
            shadow={false}
            className="w-6 h-6 rounded-md shrink-0"
          />
          <div className="w-px h-4 bg-gray-200 dark:bg-white/[0.07] shrink-0" />
          <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
            {filtersLeft}
            {filtersSlot}
          </div>
          {filtersRight && (
            <div className="flex items-center gap-2 shrink-0 ml-auto">{filtersRight}</div>
          )}
        </div>
      )}

      {showDivider && <GradientDivider />}
    </div>
  );
};

export default PageToolbar;
